import React, { useState, useMemo, useRef } from "react";
import "../../Styles/BoothsTable.css";
import * as XLSX from "xlsx";

import { FaBicycle } from "react-icons/fa";
import { PiFanBold } from "react-icons/pi";
import { CiGlass } from "react-icons/ci";
import { FaPlus, FaEdit, FaTrash, FaTimes, FaFileExcel, FaFileUpload } from "react-icons/fa";


import { MdOutlineFrontHand } from "react-icons/md";

const MANDAL_OPTIONS = ["All Mandals", "Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];
const STATUS_OPTIONS = ["On Time", "Delayed", "Pending"];

const INITIAL_BOOTHS = [
  { boothNo: "001", inCharge: "Ravi Kumar", votesPolled: 420, registeredVotes: 1000, pendingVotes: 580, votingPct: 42.0, tdp: 210, ysp: 150, janasena: 60,congress:30, turnout: 42, status: "On Time", mandal: "Kandukur" },
  { boothNo: "002", inCharge: "Suresh Babu", votesPolled: 380, registeredVotes: 950, pendingVotes: 570, votingPct: 40.0, tdp: 160, ysp: 180, janasena: 40,congress:30, turnout: 40, status: "On Time", mandal: "Kandukur" },
  { boothNo: "003", inCharge: "Lakshmi Narayana", votesPolled: 510, registeredVotes: 1100, pendingVotes: 590, votingPct: 46.4, tdp: 300, ysp: 170, janasena: 40,congress:30, turnout: 46.4, status: "On Time", mandal: "Gudluru" },
  { boothNo: "004", inCharge: "Prasad Reddy", votesPolled: 275, registeredVotes: 900, pendingVotes: 625, votingPct: 30.6, tdp: 90, ysp: 150, janasena: 35,congress:30, turnout: 30.6, status: "Delayed", mandal: "Ulavapadu" },
  { boothNo: "005", inCharge: "Anitha Reddy", votesPolled: 460, registeredVotes: 1200, pendingVotes: 740, votingPct: 38.3, tdp: 220, ysp: 200, janasena: 40,congress:30, turnout: 38.3, status: "On Time", mandal: "Gudluru" },
  { boothNo: "293", inCharge: "Mohammad Shareef", votesPolled: 320, registeredVotes: 980, pendingVotes: 660, votingPct: 32.6, tdp: 120, ysp: 160, janasena: 40, congress:30,turnout: 32.6, status: "On Time", mandal: "Lingasamudram" },
];

const EMPTY_FORM = {
  boothNo: "",
  inCharge: "",
  mandal: "Kandukur",
  registeredVotes: "",
  tdp: "",
  ysp: "",
  janasena: "",
   congress: "",
  status: "On Time",
};

// Valid mandal names for validation
const VALID_MANDALS = MANDAL_OPTIONS.filter((m) => m !== "All Mandals");
const VALID_STATUSES = STATUS_OPTIONS;

const getStatusClass = (status) => {
  switch (status) {
    case "On Time":
      return "booth1234-status-ontime";
    case "Delayed":
      return "booth1234-status-delayed";
    case "Pending":
      return "booth1234-status-pending";
    default:
      return "";
  }
};

// Normalizes a raw Excel row (keys can vary in case/spacing) into our booth shape
const normalizeRow = (row) => {
  // Build a lowercase, space-stripped lookup of the row's keys
  const lookup = {};
  Object.keys(row).forEach((key) => {
    const cleanKey = key.toString().trim().toLowerCase().replace(/[\s.]+/g, "");
    lookup[cleanKey] = row[key];
  });

  const get = (...aliases) => {
    for (const alias of aliases) {
      const val = lookup[alias];
      if (val !== undefined && val !== null && val !== "") return val;
    }
    return "";
  };

  const boothNo = String(get("boothno", "booth", "boothnumber") || "").trim();
  const inCharge = String(get("incharge", "inchargename", "incharge name") || "").trim();
  let mandal = String(get("mandal") || "").trim();
  let status = String(get("status") || "").trim();

  const registeredVotes = Number(get("registeredvotes", "registered")) || 0;
  const tdp = Number(get("tdp")) || 0;
  const ysp = Number(get("ysp", "ysrcp")) || 0;
  const janasena = Number(get("janasena")) || 0;
  const congress = Number(get("congress")) || 0;

  // Fall back to defaults if invalid
  if (!VALID_MANDALS.includes(mandal)) mandal = VALID_MANDALS[0];
  if (!VALID_STATUSES.includes(status)) status = "On Time";

  const votesPolled = tdp + ysp + janasena;
  const pendingVotes = Math.max(registeredVotes - votesPolled, 0);
  const votingPct = registeredVotes ? Number(((votesPolled / registeredVotes) * 100).toFixed(1)) : 0;
  const turnout = votingPct;

  return {
    boothNo,
    inCharge,
    mandal,
    registeredVotes,
    votesPolled,
    pendingVotes,
    votingPct,
    tdp,
    ysp,
    janasena,
    congress,
    turnout,
    status,
  };
};

function BoothsTable() {
  const [booths, setBooths] = useState(INITIAL_BOOTHS);
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [search, setSearch] = useState("");

  // Modal state (Add / Edit)
  const [showModal, setShowModal] = useState(false);
  const [editingBoothNo, setEditingBoothNo] = useState(null); // null = add mode
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");

  // Upload state
  const fileInputRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSummary, setUploadSummary] = useState(null); // { added, updated, skipped }
  const [isUploading, setIsUploading] = useState(false);

  const filteredBooths = useMemo(() => {
    const searchLower = search.trim().toLowerCase();
    return booths.filter((b) => {
      const matchesMandal = mandalFilter === "All Mandals" || b.mandal === mandalFilter;
      const matchesSearch =
        searchLower === "" ||
        b.boothNo.toLowerCase().includes(searchLower) ||
        b.inCharge.toLowerCase().includes(searchLower);
      return matchesMandal && matchesSearch;
    });
  }, [booths, mandalFilter, search]);

  // ---------- STATS ----------
  const totalBooths = booths.length;
  const completedCount = booths.filter((b) => b.status === "On Time").length;
  const pendingCount = booths.filter((b) => b.status === "Pending").length;
  const delayedCount = booths.filter((b) => b.status === "Delayed").length;

  const pct = (n) => (totalBooths ? ((n / totalBooths) * 100).toFixed(1) : "0.0");

  // ---------- EXPORT CSV ----------
  const handleExportCSV = () => {
    const headers = [
      "Booth No.", "In-Charge Name", "Votes Polled", "Registered Votes",
      "Pending Votes", "Voting %", "TDP", "YSP", "JANASENA", "congress","Turnout", "Status", "Mandal",
    ];
    const rows = filteredBooths.map((b) => [
      b.boothNo, b.inCharge, b.votesPolled, b.registeredVotes, b.pendingVotes,
      b.votingPct, b.tdp, b.ysp, b.janasena,b.congress, b.turnout, b.status, b.mandal,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "booth-summary.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---------- DOWNLOAD SAMPLE TEMPLATE ----------
  const handleDownloadTemplate = () => {
    const headers = ["Booth No", "In Charge", "Mandal", "Registered Votes", "TDP", "YSP", "Janasena","Congress", "Status"];
    const sampleRow = ["006", "Example Name", "Kandukur", 1000, 200, 150, 50, "On Time"];

    const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Booths");
    XLSX.writeFile(wb, "booth-upload-template.xlsx");
  };

  // ---------- MODAL HANDLERS (Add / Edit) ----------
  const openAddModal = () => {
    setEditingBoothNo(null);
    setFormData(EMPTY_FORM);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (booth) => {
    setEditingBoothNo(booth.boothNo);
    setFormData({
      boothNo: booth.boothNo,
      inCharge: booth.inCharge,
      mandal: booth.mandal,
      registeredVotes: booth.registeredVotes,
      tdp: booth.tdp,
      ysp: booth.ysp,
      janasena: booth.janasena,
      congress:booth.congress,
      status: booth.status,
    });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteBooth = (boothNo) => {
    if (window.confirm(`Are you sure you want to delete Booth No. ${boothNo}?`)) {
      setBooths((prev) => prev.filter((b) => b.boothNo !== boothNo));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const boothNo = formData.boothNo.trim();
    const inCharge = formData.inCharge.trim();
    const registeredVotes = Number(formData.registeredVotes) || 0;
    const tdp = Number(formData.tdp) || 0;
    const ysp = Number(formData.ysp) || 0;
    const janasena = Number(formData.janasena) || 0;
    const congress = Number(formData.congress) || 0;

    if (!boothNo || !inCharge) {
      setFormError("Booth No. and In-Charge Name are required.");
      return;
    }

    const duplicate = booths.some(
      (b) => b.boothNo === boothNo && b.boothNo !== editingBoothNo
    );
    if (duplicate) {
      setFormError("A booth with this Booth No. already exists.");
      return;
    }

    const votesPolled = tdp + ysp + janasena;
    const pendingVotes = Math.max(registeredVotes - votesPolled, 0);
    const votingPct = registeredVotes ? Number(((votesPolled / registeredVotes) * 100).toFixed(1)) : 0;
    const turnout = votingPct;

    const newBooth = {
      boothNo,
      inCharge,
      mandal: formData.mandal,
      registeredVotes,
      votesPolled,
      pendingVotes,
      votingPct,
      tdp,
      ysp,
      janasena,
      congress,
      turnout,
      status: formData.status,
    };

    if (editingBoothNo) {
      setBooths((prev) => prev.map((b) => (b.boothNo === editingBoothNo ? newBooth : b)));
    } else {
      setBooths((prev) => [...prev, newBooth]);
    }

    setShowModal(false);
    setFormError("");
  };

  // ---------- UPLOAD HANDLERS ----------
  const openUploadModal = () => {
    setUploadError("");
    setUploadSummary(null);
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadError("");
    setUploadSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExt = /\.(xlsx|xls|csv)$/i;
    if (!allowedExt.test(file.name)) {
      setUploadError("Please upload a valid .xlsx, .xls, or .csv file.");
      return;
    }

    setIsUploading(true);
    setUploadError("");
    setUploadSummary(null);

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const jsonRows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (!jsonRows.length) {
          setUploadError("The uploaded file is empty or has no readable rows.");
          setIsUploading(false);
          return;
        }

        let added = 0;
        let updated = 0;
        let skipped = 0;

        setBooths((prevBooths) => {
          const boothMap = new Map(prevBooths.map((b) => [b.boothNo, b]));

          jsonRows.forEach((row) => {
            const normalized = normalizeRow(row);

            // Skip rows without a booth number or in-charge name
            if (!normalized.boothNo || !normalized.inCharge) {
              skipped++;
              return;
            }

            if (boothMap.has(normalized.boothNo)) {
              updated++;
            } else {
              added++;
            }
            boothMap.set(normalized.boothNo, normalized);
          });

          return Array.from(boothMap.values());
        });

        setUploadSummary({ added, updated, skipped, total: jsonRows.length });
      } catch (err) {
        setUploadError("Could not read the file. Please check the format and try again.");
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    reader.onerror = () => {
      setUploadError("Failed to read the file. Please try again.");
      setIsUploading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="booth1234-page">
      {/* Header */}
      <div className="booth1234-header">
        <div className="booth1234-header-left">
          <div className="booth1234-header-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="10" width="4" height="10" rx="1" fill="currentColor" />
              <rect x="10" y="6" width="4" height="14" rx="1" fill="currentColor" />
              <rect x="17" y="3" width="4" height="17" rx="1" fill="currentColor" />
            </svg>
          </div>
          <h2 className="booth1234-title">Booth Summary</h2>
        </div>

        <div className="booth1234-header-actions">
          <button className="booth1234-upload-btn" onClick={openUploadModal}>
            <FaFileExcel size={14} />
            Upload Booths List Excel File
          </button>
          <button className="booth1234-add-btn" onClick={openAddModal}>
            <FaPlus size={13} />
            Add Booth
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="booth1234-filters">
        <div className="booth1234-filter-group">
          <label className="booth1234-filter-label">Select Mandal</label>
          <select
            className="booth1234-select"
            value={mandalFilter}
            onChange={(e) => setMandalFilter(e.target.value)}
          >
            {MANDAL_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div className="booth1234-filter-group booth1234-filter-search">
          <label className="booth1234-filter-label">Search Booth No. / In-charge</label>
          <div className="booth1234-search-box">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <button className="booth1234-export-btn" onClick={handleExportCSV}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Stat Cards */}
      <div className="booth1234-stats-grid">
        <div className="booth1234-stat-card booth1234-stat-blue">
          <div className="booth1234-stat-icon booth1234-icon-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M3 21h18M4 21V9l8-6 8 6v12M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="booth1234-stat-text">
            <span className="booth1234-stat-label">Total Booths</span>
            <span className="booth1234-stat-value">{totalBooths}</span>
          </div>
        </div>

        <div className="booth1234-stat-card booth1234-stat-green">
          <div className="booth1234-stat-icon booth1234-icon-green">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="booth1234-stat-text">
            <span className="booth1234-stat-label">Completed</span>
            <span className="booth1234-stat-value">
              {completedCount} <span className="booth1234-stat-pct">({pct(completedCount)}%)</span>
            </span>
          </div>
        </div>

        <div className="booth1234-stat-card booth1234-stat-orange">
          <div className="booth1234-stat-icon booth1234-icon-orange">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="booth1234-stat-text">
            <span className="booth1234-stat-label">Pending</span>
            <span className="booth1234-stat-value">
              {pendingCount} <span className="booth1234-stat-pct">({pct(pendingCount)}%)</span>
            </span>
          </div>
        </div>

        <div className="booth1234-stat-card booth1234-stat-red">
          <div className="booth1234-stat-icon booth1234-icon-red">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              <path d="M10.3 3.9L2.6 18a2 2 0 001.7 3h15.4a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="booth1234-stat-text">
            <span className="booth1234-stat-label">Delayed</span>
            <span className="booth1234-stat-value">
              {delayedCount} <span className="booth1234-stat-pct">({pct(delayedCount)}%)</span>
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="booth1234-table-wrapper">
        <table className="booth1234-table  table table-hover">
          <thead>
            <tr>
              <th>Booth No.</th>
              <th>In-Charge Name</th>
              <th>Votes Polled</th>
              <th>Registered Votes</th>
              <th>Pending Votes</th>
              <th>Voting %</th>
              <th className="booth1234-col-tdp" ><FaBicycle />TDP</th>
              <th className="booth1234-col-ysp"><PiFanBold />YSRCP</th>
              <th className="booth1234-col-janasena"><CiGlass />JANASENA</th>
              <th className="booth1234-col-congress"><MdOutlineFrontHand />Congress</th>
              <th>Turnout</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredBooths.length === 0 ? (
              <tr>
                <td colSpan="12" className="booth1234-no-data">
                  No booths found.
                </td>
              </tr>
            ) : (
              filteredBooths.map((b) => (
                <tr key={b.boothNo}>
                  <td className="booth1234-booth-no">{b.boothNo}</td>
                  <td>{b.inCharge}</td>
                  <td>{b.votesPolled.toLocaleString()}</td>
                  <td>{b.registeredVotes.toLocaleString()}</td>
                  <td>{b.pendingVotes.toLocaleString()}</td>
                  <td>{b.votingPct}%</td>
                  <td className="booth1234-col-tdp">{b.tdp}</td>
                  <td className="booth1234-col-ysp">{b.ysp}</td>
                  <td className="booth1234-col-janasena">{b.janasena}</td>
                  <td className="booth1234-col-janasena">{b.congress}</td>
                  <td>{b.turnout}%</td>
                  <td>
                    <span className={`booth1234-status-badge ${getStatusClass(b.status)}`}>
                      <span className="booth1234-status-dot" />
                      {b.status}
                    </span>
                  </td>
                  <td>
                    <div className="booth1234-action-btns">
                      <button
                        className="booth1234-icon-btn booth1234-edit-btn"
                        onClick={() => openEditModal(b)}
                        title="Edit Booth"
                      >
                        <FaEdit size={13} />
                      </button>
                      <button
                        className="booth1234-icon-btn booth1234-delete-btn"
                        onClick={() => handleDeleteBooth(b.boothNo)}
                        title="Delete Booth"
                      >
                        <FaTrash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer note */}
      <div className="booth1234-footer">
        <div className="booth1234-footer-left">
          <div className="booth1234-footer-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 18h6M10 21h4M12 3a6 6 0 00-3.5 10.9c.5.4.8 1 .8 1.6v.5h5.4v-.5c0-.6.3-1.2.8-1.6A6 6 0 0012 3z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            </svg>
          </div>
          <p>
            View all {totalBooths} booths, filter by Mandal, sort and identify booths with missing or slow updates.
          </p>
        </div>

        <div className="booth1234-footer-tagline">
          Accurate Data.<br />Stronger Democracy.
        </div>
      </div>

      {/* Add / Edit Booth Modal */}
      {showModal && (
        <div className="booth1234-modal-overlay" onClick={closeModal}>
          <div className="booth1234-modal" onClick={(e) => e.stopPropagation()}>
            <div className="booth1234-modal-header">
              <h3>{editingBoothNo ? "Edit Booth" : "Add Booth"}</h3>
              <button className="booth1234-modal-close" onClick={closeModal}>
                <FaTimes size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="booth1234-modal-form">
              {formError && <div className="booth1234-form-error">{formError}</div>}

              <div className="booth1234-form-row">
                <div className="booth1234-form-group">
                  <label>Booth No.</label>
                  <input
                    type="text"
                    name="boothNo"
                    value={formData.boothNo}
                    onChange={handleFormChange}
                    disabled={!!editingBoothNo}
                    placeholder="e.g. 006"
                  />
                </div>

                <div className="booth1234-form-group">
                  <label>In-Charge Name</label>
                  <input
                    type="text"
                    name="inCharge"
                    value={formData.inCharge}
                    onChange={handleFormChange}
                    placeholder="e.g. Ramesh Kumar"
                  />
                </div>
              </div>

              <div className="booth1234-form-row">
                <div className="booth1234-form-group">
                  <label>Mandal</label>
                  <select name="mandal" value={formData.mandal} onChange={handleFormChange}>
                    {VALID_MANDALS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>

                <div className="booth1234-form-group">
                  <label>Status</label>
                  <select name="status" value={formData.status} onChange={handleFormChange}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="booth1234-form-row">
                <div className="booth1234-form-group">
                  <label>Registered Votes</label>
                  <input
                    type="number"
                    name="registeredVotes"
                    value={formData.registeredVotes}
                    onChange={handleFormChange}
                    placeholder="e.g. 1000"
                  />
                </div>
              </div>

              <div className="booth1234-form-row booth1234-form-row-3">
                <div className="booth1234-form-group">
                  <label>TDP Votes</label>
                  <input type="number" name="tdp" value={formData.tdp} onChange={handleFormChange} />
                </div>
                <div className="booth1234-form-group">
                  <label>YSRCP Votes</label>
                  <input type="number" name="ysp" value={formData.ysp} onChange={handleFormChange} />
                </div>
                <div className="booth1234-form-group">
                  <label>JANASENA Votes</label>
                  <input type="number" name="janasena" value={formData.janasena} onChange={handleFormChange} />
                </div>

                 <div className="booth1234-form-group">
                  <label>CONGRESS Votes</label>
                  <input type="number" name="janasena" value={formData.congress} onChange={handleFormChange} />
                </div>
              </div>

              <div className="booth1234-modal-actions">
                <button type="button" className="booth1234-btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="booth1234-btn-save">
                  {editingBoothNo ? "Save Changes" : "Add Booth"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Excel Modal */}
      {showUploadModal && (
        <div className="booth1234-modal-overlay" onClick={closeUploadModal}>
          <div className="booth1234-modal booth1234-upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="booth1234-modal-header">
              <h3>Upload Booth Data</h3>
              <button className="booth1234-modal-close" onClick={closeUploadModal}>
                <FaTimes size={16} />
              </button>
            </div>

            <div className="booth1234-upload-body">
              <p className="booth1234-upload-instructions">
                Upload an Excel (.xlsx / .xls) or CSV file with booth data. Existing booth numbers will
                be <strong>updated</strong>; new booth numbers will be <strong>added</strong>.
              </p>

              <div className="booth1234-upload-columns-hint">
                Expected columns: <code>Booth No</code>, <code>In Charge</code>, <code>Mandal</code>,{" "}
                <code>Registered Votes</code>, <code>TDP</code>, <code>YSP</code>, <code>Janasena</code>,{" "}
                <code>Status</code>
              </div>

              <button className="booth1234-template-btn" onClick={handleDownloadTemplate} type="button">
                <FaFileExcel size={13} />
                Download Sample Template
              </button>

              <div
                className="booth1234-dropzone"
                onClick={triggerFileSelect}
                role="button"
                tabIndex={0}
              >
                <FaFileUpload size={26} />
                <p>{isUploading ? "Processing file..." : "Click to select a file (.xlsx, .xls, .csv)"}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  hidden
                />
              </div>

              {uploadError && <div className="booth1234-form-error">{uploadError}</div>}

              {uploadSummary && (
                <div className="booth1234-upload-summary">
                  <div className="booth1234-upload-summary-item booth1234-upload-added">
                    Added: <strong>{uploadSummary.added}</strong>
                  </div>
                  <div className="booth1234-upload-summary-item booth1234-upload-updated">
                    Updated: <strong>{uploadSummary.updated}</strong>
                  </div>
                  {uploadSummary.skipped > 0 && (
                    <div className="booth1234-upload-summary-item booth1234-upload-skipped">
                      Skipped (missing data): <strong>{uploadSummary.skipped}</strong>
                    </div>
                  )}
                </div>
              )}

              <div className="booth1234-modal-actions">
                <button type="button" className="booth1234-btn-cancel" onClick={closeUploadModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BoothsTable;